import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, Upload, Download, FileSpreadsheet, CheckCircle2, 
  AlertCircle, Loader2, ChevronRight 
} from 'lucide-react';

import { getLoggedInServiceNo } from '../../utils/masterDataUtils';
import PayrollBatchEmployeeService from '../../services/PayrollBatchEmployeeService';
import { 
  BATCH_IMPORT_COLUMNS, 
  downloadBatchEmployeeTemplate, 
  readBatchWorkbook, 
  validateBatchRow 
} from '../../utils/batchEmployeeImport';

const BTN_PRIMARY = 'flex items-center gap-2 h-9 px-5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST = 'flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export default function ImportBatchEmployeesModal({ 
  isOpen, 
  onClose, 
  batches = [], 
  employees = [], 
  onImportSuccess 
}) {
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [loadingBatchData, setLoadingBatchData] = useState(false);
  const [existingBatchEmpNos, setExistingBatchEmpNos] = useState(new Set());
  
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);

  const selectedBatch = batches.find((b) => String(b.id) === String(selectedBatchId));

  // Fast-lookup map for Master Employees
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      const sno = (emp.HED_SERVICE_NO || emp.serviceNo || '').toLowerCase();
      if (sno) map[sno] = emp;
    });
    return map;
  }, [employees]);

  // Load existing employees for selected batch to prevent duplicates
  useEffect(() => {
    if (!selectedBatchId) {
      setExistingBatchEmpNos(new Set());
      return;
    }

    setLoadingBatchData(true);
    PayrollBatchEmployeeService.GetPayRollEmpByBatchId(selectedBatchId)
      .then((res) => {
        const list = res?.data?.ResultSet || [];
        const activeSet = new Set(
          list
            .filter((i) => i.HPBE_IS_ACTIVE === 'True' || i.HPBE_IS_ACTIVE === 1)
            .map((i) => String(i.HED_SERVICE_NO).toLowerCase())
        );
        setExistingBatchEmpNos(activeSet);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingBatchData(false));
  }, [selectedBatchId]);

  const reset = () => {
    setFileName('');
    setRows([]);
    setParseError(null);
    setResults(null);
    setProgress({ done: 0, total: 0 });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
      setSelectedBatchId('');
    }
  }, [isOpen]);

  const handleFile = async (file) => {
    if (!file) return;
    reset();
    setFileName(file.name);

    try {
      const parsed = await readBatchWorkbook(file);
      if (!parsed.length) {
        setParseError('No rows found in the uploaded file.');
        return;
      }

      // Count how many times each employee appears in the file
      const countMap = {};
      parsed.forEach((r) => {
        const key = String(r.empNo || '').trim().toLowerCase();
        if (key) {
          countMap[key] = (countMap[key] || 0) + 1;
        }
      });

      const duplicateInFileSet = new Set(
        Object.keys(countMap).filter((k) => countMap[k] > 1)
      )

      const validated = parsed.map((r) => {
        const { values, errors } = validateBatchRow(r, employeeMap, existingBatchEmpNos, duplicateInFileSet);
        return { _row: r._row, values, errors };
      });

      setRows(validated);
    } catch (err) {
      setParseError(err?.message || 'Could not parse the selected excel file.');
    }
  };

  const valid = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalid = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);

  const runImport = async () => {
    if (!selectedBatch) return;

    setImporting(true);
    setProgress({ done: 0, total: valid.length });

    const ok = [];
    const failed = [];
    const createdBy = getLoggedInServiceNo();

    for (let i = 0; i < valid.length; i++) {
      const row = valid[i];
      const emp = employeeMap[row.values.empNo.toLowerCase()];

      try {
        await PayrollBatchEmployeeService.AddPayRollEmp({
          batchId: selectedBatch.id,
          employeeId: emp.HED_EMPLOYEE_ID || emp.employeeId,

          amount: row.values.amount !== undefined ? row.values.amount : (selectedBatch.amount ?? 0),
          percentage: null,
          isIncluded: 1,
          isProcessed: 0,
          isActive: 1,
          overrideReason: null,
          remarks: null,
          createdBy,
        });
        ok.push(row);
      } catch (err) {
        failed.push({
          ...row,
          message: err?.response?.data?.Message || err?.message || 'Failed to add',
        });
      }

      setProgress({ done: i + 1, total: valid.length });
    }

    setResults({ ok, failed });
    setImporting(false);

    if (ok.length && onImportSuccess) {
      onImportSuccess(selectedBatch.id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={importing ? undefined : onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-primary-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Import Batch Employees</h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Bulk assign employees into a batch via spreadsheet.
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            disabled={importing}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Step 1: Batch Selection */}
          <div className="flex justify-between items-center p-4 rounded-xl border border-gray-200 space-y-2">
            <div>
              <p className="text-[13px] font-semibold text-gray-800">1. Target Batch</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Select a batch to download its template or upload the Excel file.
              </p>
            </div>
            <select
              value={selectedBatchId}
              onChange={(e) => {
                setSelectedBatchId(e.target.value);
                reset();
              }}
              disabled={importing}
              className="text-xs h-9 px-3 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-primary-500 outline-none"
            >
              <option value="">-- Choose Batch --</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.batchNo} - {b.name} ({b.calculationMethod})
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Download Template */}
          <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
            <div>
              <p className="text-[13px] font-semibold text-gray-800">2. Download Template</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Two columns only: Service No and Amount (blank amount uses the batch amount).
              </p>
            </div>
            <button
              type="button"
              disabled={!selectedBatch || loadingBatchData}
              onClick={() => downloadBatchEmployeeTemplate(selectedBatch)}
              className={BTN_GHOST}
            >
              <Download size={13} />
              Download Template
            </button>
          </div>

          {/* Step 3: Upload Excel */}
          <div className="p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[13px] font-semibold text-gray-800">3. Upload File</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {fileName ? `Selected: ${fileName}` : '.xlsx files only.'}
                </p>
              </div>
              <label className={`${BTN_GHOST} ${!selectedBatch || importing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' }`}>
                <Upload size={13} /> Choose File
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  disabled={!selectedBatch || importing}
                  onChange={(e) => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {parseError && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-[12px] text-red-700">{parseError}</p>
              </div>
            )}
          </div>

          {/* Preview Rows */}
          {rows.length > 0 && !results && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">4. Review Validation</h3>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                    <CheckCircle2 size={11} /> {valid.length} valid
                  </span>
                  {invalid.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold">
                      <AlertCircle size={11} /> {invalid.length} invalid
                    </span>
                  )}
                </div>
              </div>

              <div className="overflow-auto max-h-[30vh]">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-gray-600">Row</th>
                      <th className="px-4 py-2 text-gray-600">Service No</th>
                      <th className="px-4 py-2 text-gray-600">Service No</th>
                      <th className="px-4 py-2 text-gray-600">Employee Name</th>
                      <th className="px-4 py-2 text-gray-600 text-right">Amount</th>
                      <th className="px-4 py-2 text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => {
                      const emp = employeeMap[r.values.empNo.toLowerCase()];
                      return (
                        <tr key={r._row} className={r.errors.length ? 'bg-red-50/30' : ''}>
                          <td className="px-4 py-2 text-gray-400">{r._row}</td>
                          <td className="px-4 py-2 font-medium text-gray-800">{r.values.empNo}</td>
                          <td className="px-4 py-2 text-gray-600">
                            {emp ? (emp.HED_REPORT_NAME || `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`) : '—'}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700">
                            {r.values.amount !== undefined && !Number.isNaN(r.values.amount)
                              ? Number(r.values.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : <span className="text-gray-400">Batch amount</span>}
                          </td>
                          <td className="px-4 py-2">
                            {r.errors.length === 0 ? (
                              <span className="text-emerald-700 font-semibold inline-flex items-center gap-1 text-[11px]">
                                <CheckCircle2 size={12} /> Ready
                              </span>
                            ) : (
                              <div className="text-red-700 text-[11px] space-y-0.5">
                                {r.errors.map((e, idx) => (
                                  <div key={idx}>• {e}</div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Results */}
          {results && (
            <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50">
              <div className="flex items-center gap-2 text-[13px] text-emerald-800 font-semibold">
                <CheckCircle2 size={16} className="text-emerald-600" />
                {results.ok.length} employees imported successfully.
              </div>
              {results.failed.length > 0 && (
                <div className="text-xs text-red-700">
                  <p className="font-semibold">{results.failed.length} rows failed:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    {results.failed.map((f, i) => (
                      <li key={i}>Row {f._row} ({f.values.empNo}): {f.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <div>
            {importing && (
              <span className="text-xs text-gray-500">
                Importing {progress.done} of {progress.total}…
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={importing} className={BTN_GHOST}>
              {results ? 'Close' : 'Cancel'}
            </button>
            {!results && (
              <button
                onClick={runImport}
                disabled={importing || valid.length === 0}
                className={BTN_PRIMARY}
              >
                {importing ? <Loader2 size={13} className="animate-spin" /> : <ChevronRight size={13} />}
                Import {valid.length > 0 ? `${valid.length} Employees` : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}